import defaultCaregiverPhoto from 'assets/experts-man-dark.png';
import { Avatar, AvatarFallback, AvatarImage } from 'components/ui/avatar';
import dayjs from 'dayjs';
import { cn } from 'lib/utils';
import { CheckCheckIcon, CheckIcon, ReplyIcon } from 'lucide-react';
import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ConversationMessage } from 'services/api/models/ConversationDTO';
import { GGApiSender } from 'services/api/models/GGApiMessage';
import { UserDTO } from 'services/api/models/UserDTO';
import { MessageSummary } from 'store/conversations/types';

export type MessageBubbleProps = {
  message: ConversationMessage;
  currentUser: UserDTO;
  conversationSummary?: MessageSummary;
  /** The sender's name was tapped (caregivers open their profile). */
  onTitleClick?: (senderId: string) => void;
  /** The quoted message was tapped. */
  onReplyClick?: (repliedToMessageId: number) => void;
  /**
   * A link in someone else's message was tapped by a caregiver, who confirms
   * before leaving the app. Without it links open directly.
   */
  onLinkClick?: (href: string) => void;
};

const URL_REGEX = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

function isValidAndSafeUrl(urlString: string): boolean {
  try {
    const url = new URL(urlString.startsWith('www.') ? `https://${urlString}` : urlString);
    return ['http:', 'https:'].includes(url.protocol) && Boolean(url.hostname);
  } catch {
    return false;
  }
}

/** The message text with its URLs as links. */
function Linkified({ text, onLinkClick }: { text: string; onLinkClick?: (href: string) => void }) {
  const parts = text.split(URL_REGEX);
  return (
    <>
      {parts.map((part, index) => {
        if (index % 2 === 0 || !isValidAndSafeUrl(part)) return <Fragment key={index}>{part}</Fragment>;
        const href = part.startsWith('www.') ? `https://${part}` : part;
        return (
          <a
            key={index}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="break-all underline"
            onClick={event => {
              if (onLinkClick) {
                event.preventDefault();
                onLinkClick(href);
              }
            }}
          >
            {part}
          </a>
        );
      })}
    </>
  );
}

function isRead(message: ConversationMessage, currentUser: UserDTO, conversationSummary?: MessageSummary) {
  if (!currentUser.is_caregiver) return false;
  const patientId = conversationSummary?.conversation.patient.id;
  const readStatus = conversationSummary?.conversation.read_statuses?.find(status => status.reader === patientId);
  return Boolean(readStatus && readStatus.last_read_datetime >= message.sent_at);
}

const senderPhoto = (sender?: GGApiSender) =>
  sender?.is_caregiver ? sender.photo_url || defaultCaregiverPhoto : sender?.photo_url;

/**
 * One message of a conversation (what react-chat-elements' `MessageBox` was):
 * the sender's bubble on the left with their name and avatar, the user's own
 * on the right, each on its `chat-*` tokens. `data-slot` and `data-position`
 * are the handles of the swipe-to-reply and context-menu hooks and of the
 * functional test suite.
 */
export default function MessageBubble({
  message,
  currentUser,
  conversationSummary,
  onTitleClick,
  onReplyClick,
  onLinkClick,
}: MessageBubbleProps) {
  const { t } = useTranslation();
  const isMine = message.sender?.id === currentUser.id;
  const position = isMine ? 'right' : 'left';
  // Another caregiver's message, seen by a caregiver: neither "me" nor the patient
  const isFromOtherCaregiver = currentUser.is_caregiver && !isMine && Boolean(message.sender?.is_caregiver);
  const photo = isMine ? undefined : senderPhoto(message.sender);
  const reply = message.replied_to_message;
  const read = isRead(message, currentUser, conversationSummary);

  return (
    <div
      data-slot="message"
      data-position={position}
      data-message-id={message.id}
      className={cn('flex w-full touch-pan-y px-4', isMine ? 'justify-end' : 'justify-start')}
    >
      <div
        data-slot="message-bubble"
        data-position={position}
        className={cn(
          'group/bubble relative max-w-[80%] rounded-lg px-3.5 pt-3.5 pb-3 whitespace-pre-line shadow-xs will-change-transform',
          isMine ? 'bg-chat-me text-chat-me-foreground' : 'bg-chat-them text-chat-them-foreground',
          isFromOtherCaregiver && 'bg-muted text-muted-foreground',
        )}
      >
        {/* The reply hint revealed while the bubble is swiped (see useSwipeToReply) */}
        <ReplyIcon
          aria-hidden
          className="pointer-events-none absolute top-1/2 -left-8 size-6 -translate-y-1/2 opacity-0 transition-opacity group-data-[swipe=active]/bubble:opacity-50"
        />
        {!isMine && message.sender?.display_name && (
          <button
            type="button"
            data-slot="message-title"
            className="mb-1 flex cursor-pointer items-center gap-2 text-sm font-medium text-chat-them-title"
            onClick={() => onTitleClick?.(message.sender.id)}
          >
            {photo && (
              <Avatar size="sm">
                <AvatarImage src={photo} />
                <AvatarFallback />
              </Avatar>
            )}
            {message.sender.display_name}
          </button>
        )}
        {reply && !message.is_deleted && (
          <button
            type="button"
            data-slot="message-reply"
            className={cn(
              // the bar is a pseudo-element: a left border would curve with the corners
              'relative mb-2 block w-full cursor-pointer overflow-hidden rounded-md px-3 py-2 text-left text-sm before:absolute before:inset-y-0 before:left-0 before:w-1 before:bg-chat-them-title',
              isMine ? 'bg-accent text-foreground' : 'bg-muted',
            )}
            onClick={() => onReplyClick?.(reply.id)}
          >
            <span className="block font-medium text-chat-them-title">{reply.sender.display_name}</span>
            <span className="line-clamp-2 text-muted-foreground">
              {reply.is_deleted ? t('chats.messageDeleted') : reply.message_text}
            </span>
          </button>
        )}
        {/* wrap-anywhere: a long URL or word breaks instead of stretching the bubble */}
        <div data-slot="message-text" className="leading-snug wrap-anywhere">
          {message.is_deleted ? (
            <em className="text-base text-subtle-foreground">{t('chats.messageDeleted')}</em>
          ) : (
            <Linkified text={message.message_text} onLinkClick={!isMine ? onLinkClick : undefined} />
          )}
        </div>
        <div
          data-slot="message-time"
          className={cn(
            'mt-1 flex items-center justify-end gap-2 text-xs',
            isMine ? 'text-chat-me-foreground/70' : 'text-muted-foreground',
          )}
        >
          {/* `gg-edited-label` is a selector of the functional test suite. */}
          {message.edited_at && !message.is_deleted && (
            <span className="gg-edited-label">{t('chats.messageEdited')}</span>
          )}
          <span>{dayjs(message.sent_at).fromNow()}</span>
          {isMine &&
            (read ? (
              <CheckCheckIcon aria-label={t('chats.status.read')} className="size-3.5" />
            ) : (
              <CheckIcon aria-label={t('chats.status.sent')} className="size-3.5" />
            ))}
        </div>
      </div>
    </div>
  );
}
